import { useId, useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { ApiError } from "@/api/client";
import {
  useCheckProvider,
  useCreateProvider,
  useDefaultModel,
  useDeleteProvider,
  useModels,
  useProviderKinds,
  useProviders,
  useUpdateProvider,
  type ProviderKindInfo,
  type ProviderSummary,
} from "@/api/queries";
import { ModelCatalog } from "@/components/providers/ModelCatalog";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Chip, StatusDot, type Tone } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field } from "@/components/ui/Field";
import { Select, Toggle } from "@/components/ui/Select";
import {
  AlertIcon,
  ChartIcon,
  ChevronRightIcon,
  PlugIcon,
  RefreshIcon,
  TrashIcon,
} from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * Models and providers (PRD FR-3.1 – FR-3.6).
 *
 * Five kinds, one shape. A kind publishes what configuring it needs — a key, a
 * base URL, a region, an auth mode — and this page renders that rather than
 * knowing any provider by name, which is what keeps "adding a provider is
 * configuration" true on the front end as well as the back.
 *
 * Health is the first thing on every card because FR-3.6 wants it wherever a
 * model is chosen, and because the three failure modes need different fixes:
 * `auth failed` is a key, `unreachable` is a URL or a network, and `no models`
 * on a local runner is an `ollama pull`.
 */

const STATUS_TONE: Record<string, Tone> = {
  ok: "success",
  no_models: "warning",
  auth_failed: "error",
  unreachable: "error",
  not_configured: "warning",
};

const STATUS_LABEL: Record<string, string> = {
  ok: "Reachable",
  no_models: "No models",
  auth_failed: "Auth failed",
  unreachable: "Unreachable",
  not_configured: "Not configured",
};

const AUTH_MODES: Array<{ value: string; label: string; hint: string }> = [
  {
    value: "default",
    label: "The AWS SDK's own chain",
    hint: "Environment variables, an instance role, or an SSO session. Nothing is stored here.",
  },
  { value: "keys", label: "Access key pair", hint: "The secret access key is encrypted at rest." },
  { value: "profile", label: "Named profile", hint: "Needs ~/.aws mounted into the container." },
  { value: "api_key", label: "Bedrock API key", hint: "A long-lived bearer key for Bedrock." },
];

/** Bedrock's fields, rendered from the kind's declared option keys. */
function BedrockOptions({
  provider,
  onChange,
}: {
  provider: ProviderSummary;
  onChange: (options: Record<string, string>) => void;
}) {
  const options = provider.options as Record<string, string>;
  const mode = options.auth_mode ?? "default";
  const [region, setRegion] = useState(options.region ?? "");
  const [keyId, setKeyId] = useState(options.aws_access_key_id ?? "");
  const [profile, setProfile] = useState(options.aws_profile ?? "");
  const selected = AUTH_MODES.find((entry) => entry.value === mode);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Region"
          value={region}
          placeholder="us-west-2"
          onChange={(event) => setRegion(event.target.value)}
          onBlur={() => onChange({ region })}
          hint="Which region's models are listed and called."
        />
        <Select
          label="Authentication"
          value={mode}
          onChange={(event) => onChange({ auth_mode: event.target.value })}
          hint={selected?.hint}
        >
          {AUTH_MODES.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </Select>
      </div>

      {mode === "keys" ? (
        <Field
          label="Access key ID"
          value={keyId}
          placeholder="AKIA…"
          onChange={(event) => setKeyId(event.target.value)}
          onBlur={() => onChange({ aws_access_key_id: keyId })}
          hint="An identifier, like a database username. Its secret goes in the key field below."
        />
      ) : null}

      {mode === "profile" ? (
        <Field
          label="Profile name"
          value={profile}
          placeholder="default"
          onChange={(event) => setProfile(event.target.value)}
          onBlur={() => onChange({ aws_profile: profile })}
        />
      ) : null}
    </div>
  );
}

function ProviderCard({
  provider,
  kind,
}: {
  provider: ProviderSummary;
  kind?: ProviderKindInfo;
}) {
  const update = useUpdateProvider(provider.id);
  const check = useCheckProvider();
  const remove = useDeleteProvider();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [key, setKey] = useState("");
  const bodyId = useId();
  const status = provider.last_status ?? "not_configured";
  const variant = (provider.options as Record<string, string>).prompt_variant ?? "full";
  const structured = (provider.options as Record<string, string>).structured_output ?? "auto";

  // Enough to decide whether this is the card you wanted, without opening it.
  // Every value is already on the provider summary, so a collapsed page costs
  // no extra requests — the per-provider catalog query lives inside
  // ModelCatalog, which is not mounted until the card is open.
  const facts = [
    kind?.label ?? provider.kind,
    `${provider.model_count} model${provider.model_count === 1 ? "" : "s"}`,
    provider.has_api_key ? "key stored" : kind?.requires_api_key ? "no key" : null,
    provider.enabled ? null : "disabled",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="hover:bg-hover motion-fast flex w-full items-start gap-3 px-5 py-4 text-start transition-colors"
      >
        <ChevronRightIcon
          className={cn(
            "text-ink-disabled motion-medium mt-1 size-4 shrink-0 transition-transform",
            open && "rotate-90",
          )}
        />
        <span className="min-w-0 flex-1">
          <span className="type-h4 text-ink block truncate">{provider.name}</span>
          <span className="type-support text-ink-secondary mt-0.5 block truncate">{facts}</span>
          {/* FR-3.6 wants health wherever a model is chosen, and the three
              failure modes need different fixes — so the diagnostic stays
              readable without opening the card. Only while closed: the body
              renders it too, and saying it twice reads as a bug. */}
          {!open && status !== "ok" && provider.last_detail ? (
            <span
              className={cn(
                "type-support mt-1 block truncate",
                STATUS_TONE[status] === "error" ? "text-error" : "text-warning",
              )}
            >
              {provider.last_detail}
            </span>
          ) : null}
        </span>
        <span className="mt-1 flex shrink-0 items-center gap-2">
          <StatusDot tone={STATUS_TONE[status] ?? "neutral"} />
          <span className="type-support text-ink-secondary hidden sm:inline">
            {STATUS_LABEL[status] ?? status}
          </span>
        </span>
      </button>

      {open ? (
        <div id={bodyId} className="border-border flex flex-col gap-5 border-t px-5 py-5">
          {provider.last_detail ? (
            <p className="type-support text-ink-secondary">{provider.last_detail}</p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            {provider.base_url ? <Chip tone="neutral">{provider.base_url}</Chip> : null}
            {provider.has_api_key ? <Chip tone="success">key stored</Chip> : null}
            {kind?.local ? <Chip tone="accent">local</Chip> : null}
            <Chip tone="neutral">discovery: {kind?.discovery ?? "—"}</Chip>
          </div>

          {provider.kind === "bedrock" ? (
            <BedrockOptions
              provider={provider}
              onChange={(options) => update.mutate({ options })}
            />
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            {kind?.requires_api_key || provider.kind === "bedrock" ? (
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Field
                    label={provider.kind === "bedrock" ? "Secret access key" : "Replace API key"}
                    revealable
                    value={key}
                    placeholder={provider.has_api_key ? "•••••••• stored" : "Paste a key"}
                    onChange={(event) => setKey(event.target.value)}
                  />
                </div>
                <Button
                  size="lg"
                  onClick={() => update.mutate({ api_key: key }, { onSuccess: () => setKey("") })}
                  disabled={!key}
                  loading={update.isPending}
                >
                  Save
                </Button>
              </div>
            ) : null}

            <Select
              label="Default prompt variant"
              value={variant}
              onChange={(event) =>
                update.mutate({ options: { prompt_variant: event.target.value } })
              }
              hint="What a model here gets unless its catalog row says otherwise. The compact variant suits small local models; a large one has no reason to be on it."
            >
              <option value="full">full — the complete platform prompt</option>
              <option value="compact">compact — short-context models</option>
            </Select>

            <Select
              label="Structured output"
              value={structured}
              onChange={(event) =>
                update.mutate({ options: { structured_output: event.target.value } })
              }
              hint={
                provider.kind === "openai_compatible"
                  ? "Whether to make the provider enforce the response schema. Automatic declines here: a generic endpoint reports the plain OpenAI profile whatever is behind it, and one that accepts the request and then stops calling tools produces no answer at all. Turn it on only for a gateway you have measured."
                  : "Whether to make the provider enforce the response schema. Automatic follows the model's own profile, which is the right answer for a provider that publishes one. Off falls back to the contract prompt and the repair ladder."
              }
            >
              <option value="auto">automatic — follow the model's profile</option>
              <option value="on">on — the provider enforces the schema</option>
              <option value="off">off — the contract is parsed from plain text</option>
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Toggle
              label="Enabled"
              checked={provider.enabled}
              onChange={(checked) => update.mutate({ enabled: checked })}
            />
            <span className="flex-1" />
            <Button
              size="sm"
              onClick={() => check.mutate(provider.id)}
              loading={check.isPending}
              icon={<RefreshIcon className="size-3.5" />}
            >
              Check &amp; refresh models
            </Button>
            <Button
              size="sm"
              variant={confirming ? "primary" : "ghost"}
              onClick={() => {
                if (confirming) remove.mutate(provider.id);
                else setConfirming(true);
              }}
              onBlur={() => setConfirming(false)}
              icon={<TrashIcon className="size-3.5" />}
            >
              {confirming ? "Confirm delete" : "Delete"}
            </Button>
          </div>

          <ModelCatalog providerId={provider.id} />
        </div>
      ) : null}
    </Card>
  );
}

function AddProvider({ kinds, defaultOpen }: { kinds: ProviderKindInfo[]; defaultOpen: boolean }) {
  const create = useCreateProvider();
  const [open, setOpen] = useState(defaultOpen);
  const formId = useId();
  const [kindKey, setKindKey] = useState(kinds[0]?.key ?? "anthropic");
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState(kinds[0]?.default_base_url ?? "");
  const [apiKey, setApiKey] = useState("");
  const [region, setRegion] = useState("us-west-2");
  const [error, setError] = useState<string | null>(null);
  const kind = kinds.find((entry) => entry.key === kindKey);

  function pickKind(key: string) {
    setKindKey(key);
    const next = kinds.find((entry) => entry.key === key);
    setBaseUrl(next?.default_base_url ?? "");
    if (!name) setName(next?.label ?? "");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        name: name.trim() || kind?.label || kindKey,
        kind: kindKey as "anthropic" | "openai" | "bedrock" | "ollama" | "openai_compatible",
        base_url: baseUrl.trim() || null,
        api_key: apiKey.trim() || null,
        enabled: true,
        default_model: null,
        options: kindKey === "bedrock" ? { region: region.trim() || "us-west-2" } : {},
      });
      setName("");
      setApiKey("");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not add this provider.");
    }
  }

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={formId}
        className="hover:bg-hover motion-fast flex w-full items-center gap-3 px-5 py-4 text-start transition-colors"
      >
        <ChevronRightIcon
          className={cn(
            "text-ink-disabled motion-medium size-4 shrink-0 transition-transform",
            open && "rotate-90",
          )}
        />
        <span className="min-w-0 flex-1">
          <span className="type-h4 text-ink block">Add a provider</span>
          {open && kind?.summary ? (
            <span className="type-support text-ink-secondary mt-0.5 block">{kind.summary}</span>
          ) : null}
        </span>
      </button>

      {open ? (
        <div id={formId} className="border-border border-t px-5 py-5">
          <form onSubmit={submit} className="flex flex-col gap-4">
            <Select
              label="Kind"
              value={kindKey}
              onChange={(event) => pickKind(event.target.value)}
              hint={kind ? `Model discovery: ${kind.discovery}` : undefined}
            >
              {kinds.map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {entry.label}
                </option>
              ))}
            </Select>

            <Field
              label="Name"
              value={name}
              placeholder={kind?.label ?? ""}
              onChange={(event) => setName(event.target.value)}
              hint="How this provider appears in the model picker."
            />

            {kindKey === "bedrock" ? (
              <Field
                label="Region"
                value={region}
                onChange={(event) => setRegion(event.target.value)}
                hint="Bedrock authenticates through the AWS SDK — keys, a profile or an instance role. Configure which after adding it."
              />
            ) : (
              <Field
                label={kind?.requires_base_url ? "Base URL" : "Base URL (optional)"}
                value={baseUrl}
                onChange={(event) => setBaseUrl(event.target.value)}
                hint={kind?.base_url_help}
              />
            )}

            {kind?.requires_api_key ? (
              <Field
                label="API key"
                revealable
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
                hint="Encrypted at rest and never returned by the API."
              />
            ) : null}

            {error ? (
              <p className="bg-error-subtle text-error type-support rounded-element flex items-start gap-2 px-3 py-2">
                <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
                {error}
              </p>
            ) : null}

            <Button type="submit" variant="primary" loading={create.isPending} className="self-start">
              Add provider
            </Button>
          </form>
        </div>
      ) : null}
    </Card>
  );
}

export function ProvidersPage() {
  const { data: providers } = useProviders();
  const { data: kinds } = useProviderKinds();
  const { data: models } = useModels();
  const { data: fallback } = useDefaultModel();

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8">
      <header className="mb-8">
        <p className="type-caption text-ink-disabled">Admin</p>
        <h1 className="type-h1 text-ink mt-2">Models &amp; providers</h1>
        <p className="type-body text-ink-secondary mt-2 max-w-2xl">
          Anthropic, OpenAI, AWS Bedrock, a local Ollama runner, or any OpenAI-compatible
          endpoint — LM Studio, vLLM, a LiteLLM proxy. Keys are encrypted at rest and never
          returned. Model choice is data: hiding a model, renaming it, pricing it and setting the
          deployment default are all edits below, never a rebuild.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {models?.length ? (
            <span className="type-support text-ink-secondary">
              {models.length} model{models.length === 1 ? "" : "s"} available to the{" "}
              <Link to="/chat" className="text-accent hover:underline">
                chat picker
              </Link>
              .
            </span>
          ) : null}
          {fallback?.ref ? (
            <Chip tone="success">default · {fallback.label ?? fallback.ref}</Chip>
          ) : (
            <Chip tone="neutral">no deployment default — the first available model is used</Chip>
          )}
          <Link
            to="/settings/usage"
            className="type-support text-ink-secondary hover:text-ink inline-flex items-center gap-1"
          >
            <ChartIcon className="size-3.5" />
            Usage &amp; cost
          </Link>
        </div>
      </header>

      <div className="flex flex-col gap-5">
        {providers?.length === 0 ? (
          <EmptyState
            icon={<PlugIcon className="size-6" />}
            title="No providers yet"
            detail="Add one below. A local Ollama runner needs no key; Anthropic and OpenAI need an API key; Bedrock authenticates through the AWS SDK."
          />
        ) : (
          providers?.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              kind={kinds?.find((entry) => entry.key === provider.kind)}
            />
          ))
        )}

        {kinds?.length && providers ? (
          <AddProvider kinds={kinds} defaultOpen={providers.length === 0} />
        ) : null}
      </div>
    </div>
  );
}
